import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const CHARACTER_OPTIONS = ["EXCELLENT", "VERY GOOD", "GOOD", "SATISFACTORY"];
const STATUS_OPTIONS = ["COMPLETED", "DISCONTINUED"];

const inputDate = (value) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

function TransferCertificate() {
  const user = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const [rollNo, setRollNo] = useState("");
  const [student, setStudent] = useState(null);
  const [character, setCharacter] = useState("");
  const [status, setStatus] = useState("");
  const [collegeLeftDate, setCollegeLeftDate] = useState("");
  const [loadingStudent, setLoadingStudent] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [error, setError] = useState("");
  const [showMissingFieldsWarning, setShowMissingFieldsWarning] = useState(true);
  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  useEffect(() => {
    if (!error) return;

    const timer = setTimeout(() => {
      setError("");
    }, 5000);

    return () => clearTimeout(timer);
  }, [error]);
  useEffect(() => {
    if (!student?.missing_tc_fields?.length) return;

    const timer = setTimeout(() => {
      // hide warning
      setShowMissingFieldsWarning(false);
    }, 10000);

    return () => clearTimeout(timer);
  }, [student]);
  const fetchStudent = useCallback(
    async (value) => {
      const normalizedRoll = String(value || "").trim();
      if (!clientId || !collegeId || !normalizedRoll) {
        setStudent(null);
        return;
      }
      setLoadingStudent(true);
      setError("");
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/tc/student/${clientId}/${collegeId}/${encodeURIComponent(
            normalizedRoll
          )}`,
          { headers: getAuthHeaders() }
        );
        const data = response.data?.data;
        setStudent(data || null);
        setStatus(data?.student_status || "");
        setCollegeLeftDate(inputDate(data?.college_left_date));
        setCharacter("");
      } catch (requestError) {
        setStudent(null);
        setStatus("");
        setCollegeLeftDate("");
        setError(requestError.response?.data?.message || "Unable to load this student.");
      } finally {
        setLoadingStudent(false);
      }
    },
    [clientId, collegeId]
  );

  useEffect(() => {
    const value = String(rollNo || "").trim();
    if (!value) {
      setStudent(null);
      setStatus("");
      setCollegeLeftDate("");
      setError("");
      return undefined;
    }
    const timer = setTimeout(() => fetchStudent(value), 550);
    return () => clearTimeout(timer);
  }, [rollNo, fetchStudent]);

  const downloadPDF = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const viewExisting = async () => {
    const popup = window.open("about:blank", "_blank");
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/tc/existing/${clientId}/${collegeId}/${encodeURIComponent(
          rollNo.trim()
        )}`,
        { headers: getAuthHeaders(), responseType: "blob" }
      );
      const url = URL.createObjectURL(response.data);
      if (popup) popup.location = url;
      else downloadPDF(response.data, `TC_${student?.existing_tc?.tc_no || "existing"}.pdf`);
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (requestError) {
      if (popup) popup.close();
      setError("Unable to open the existing TC.");
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError("");
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/tc/generate`,
        {
          client_id: clientId,
          college_id: collegeId,
          student_rollno: rollNo.trim(),
          student_character: character,
          student_status: status,
          college_left_date: collegeLeftDate,
          generated_by: user?.user_email || user?.user_email_id || user?.user_name || "SYSTEM",
        },
        {
          headers: {
            ...getAuthHeaders(),
            "Cache-Control": "no-cache, no-store",
            Pragma: "no-cache",
          },
          responseType: "blob",
        }
      );
      const disposition = response.headers["content-disposition"] || "";
      const fileName =
        disposition.match(/filename="?([^";]+)"?/i)?.[1] || "Transfer_Certificate.pdf";
      downloadPDF(response.data, fileName);
      const tcNo = fileName.match(/TC_(.+)\.pdf/i)?.[1] || "";
      setStudent((current) => ({ ...current, existing_tc: { tc_no: tcNo } }));
      setPreviewOpen(false);
    } catch (requestError) {
      if (requestError.response?.status === 409) {
        setPreviewOpen(false);
        await fetchStudent(rollNo.trim());
      }
      let message = "Unable to generate the TC.";
      const data = requestError.response?.data;
      if (data instanceof Blob) {
        try {
          message = JSON.parse(await data.text()).message || message;
        } catch {
          /* retain the default */
        }
      } else {
        message = data?.message || message;
      }
      setError(message);
    } finally {
      setGenerating(false);
    }
  };

  const canView = Boolean(
    student &&
      student.student_rollno === rollNo.trim() &&
      character &&
      status &&
      collegeLeftDate &&
      !student.existing_tc &&
      (!student.missing_tc_fields || student.missing_tc_fields.length === 0) &&
      !loadingStudent
  );
  const hasCurrentStudent = Boolean(student && student.student_rollno === rollNo.trim());
  const valueText = (value) => (value == null || value === "" ? "—" : String(value));
  const formatDate = (date) => {
    if (!date) return "";

    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "";

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    return `${day}-${month}-${year}`;
  };
  return (
    <MDBox p={2} sx={{ width: "100%", minWidth: 0 }}>
      <MDBox
        mb={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
      >
        <MDTypography variant="h5">PRINT TRANSFER CERTIFICATE</MDTypography>
      </MDBox>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {showMissingFieldsWarning && student?.missing_tc_fields?.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          TC cannot be generated until these student details are filled in the student record:{" "}
          {student.missing_tc_fields.join(", ")}.
        </Alert>
      )}

      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            label="Student Roll No"
            value={rollNo}
            onChange={(event) => setRollNo(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") fetchStudent(rollNo);
            }}
            placeholder="Enter exact roll number"
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            select
            fullWidth
            label="Student Character"
            value={character}
            onChange={(event) => setCharacter(event.target.value)}
            disabled={!student}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: true }}
          >
            <option value="">Select Character</option>
            {CHARACTER_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} md={2}>
          <TextField
            select
            fullWidth
            label="Student Status"
            value={status}
            onChange={(event) => {
              const nextStatus = event.target.value;
              setStatus(nextStatus);

              if (nextStatus === "DISCONTINUED") {
                setCollegeLeftDate(inputDate(student?.college_left_date));
              } else {
                setCollegeLeftDate("");
              }
            }}
            disabled={!student}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: true }}
          >
            <option value=""></option>
            {STATUS_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            type="date"
            label="College Left Date"
            value={collegeLeftDate}
            onChange={(event) => setCollegeLeftDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={!student || (status === "DISCONTINUED" && Boolean(student.college_left_date))}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <Box
            display="flex"
            justifyContent={{ xs: "stretch", md: "flex-end" }}
            gap={1}
            flexWrap="wrap"
          >
            {loadingStudent && <CircularProgress size={28} />}
            {hasCurrentStudent && student?.existing_tc ? (
              <MDButton variant="gradient" color="info" onClick={viewExisting}>
                View Existing TC
              </MDButton>
            ) : (
              <MDButton
                variant="gradient"
                color="info"
                disabled={!canView}
                onClick={() => setPreviewOpen(true)}
              >
                View TC
              </MDButton>
            )}
          </Box>
        </Grid>
      </Grid>

      {hasCurrentStudent && (
        <Box
          mt={2}
          p={2}
          sx={{
            border: "1px solid #d9dce1",
            borderRadius: 1,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 1.25,
          }}
        >
          {[
            ["Student Name", student.student_name],
            ["Roll No", student.student_rollno],
            ["Admission No", student.admission_no],
            ["Program", student.program_name],
            ["Branch", student.branch_name],
            ["Year", student.year_number],
            ["Semester", student.semester_number],
            ["Status", status],
            [
              "College Left Date",
              status === "DISCONTINUED"
                ? inputDate(student.college_left_date) || collegeLeftDate
                : collegeLeftDate,
            ],
            [
              "Next TC Serial No",
              student.existing_tc ? student.existing_tc.tc_no : student.next_tc_no,
            ],
          ].map(([label, value]) => (
            <Box key={label} sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
              <MDTypography variant="caption" color="text">
                {label}
              </MDTypography>
              <MDTypography variant="button" display="block">
                {valueText(value)}
              </MDTypography>
            </Box>
          ))}
        </Box>
      )}

      <Dialog
        open={previewOpen}
        onClose={() => !generating && setPreviewOpen(false)}
        fullWidth
        maxWidth="md"
        sx={{
          "& .MuiDialogTitle-root": {
            color: "black",
          },
          "& .MuiDialogContent-root": {
            color: "black",
          },
          "& .MuiDialogContent-root .MuiTypography-root": {
            color: "black",
          },
        }}
      >
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pr: 1 }}
        >
          TC Preview
          <IconButton
            color="error"
            aria-label="Close preview"
            onClick={() => setPreviewOpen(false)}
            disabled={generating}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ color: "black" }}>
          {hasCurrentStudent && (
            <Box
              sx={{
                display: "grid",
                color: "black",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.5,
              }}
            >
              {[
                ["Transfer Certificate No", student.next_tc_no],
                ["Student Name", student.student_name],
                ["Father Name", student.father_name],
                [
                  "Nationality / Religion / Caste",
                  [student.nationality, student.religion, student.caste, student.subcaste]
                    .filter(Boolean)
                    .join(", "),
                ],
                ["Date of Birth", formatDate(student.dob)],
                ["Admission Date", formatDate(student.admission_date)],
                ["Admission No", student.admission_no],
                ["Roll No", student.student_rollno],
                [
                  "Program / Branch",
                  `${student.program_name || ""} / ${student.branch_name || ""}`,
                ],
                [
                  "Class at Leaving",
                  `Year ${student.year_number}, Semester ${student.semester_number}`,
                ],
                ["Student Status", status],
                [
                  "College Left Date",
                  status === "DISCONTINUED"
                    ? formatDate(student.college_left_date) || formatDate(collegeLeftDate)
                    : formatDate(collegeLeftDate),
                ],
                ["Qualified for Promotion", status === "COMPLETED" ? "YES" : "NO"],
                ["Fees Paid", "YES"],
                ["Applied for Certificate", "YES"],
                ["Application Date", new Date().toLocaleDateString("en-GB")],
                ["Conduct & Character", character],
              ].map(([label, value]) => (
                <Box key={label} sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                  <MDTypography variant="caption" color="black">
                    {label}
                  </MDTypography>
                  <MDTypography variant="button" display="block" color="black">
                    {valueText(value)}
                  </MDTypography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, flexWrap: "wrap", gap: 1 }}>
          <MDButton
            variant="outlined"
            color="error"
            startIcon={<CloseIcon />}
            onClick={() => setPreviewOpen(false)}
            disabled={generating}
          >
            Cancel
          </MDButton>
          <MDButton variant="gradient" color="info" onClick={handleGenerate} disabled={generating}>
            {generating ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Confirm & Generate TC PDF"
            )}
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}

export default TransferCertificate;
